import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Gauge, RefreshCw, AlertCircle } from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  BarChart,
  Bar
} from 'recharts';
import { api as axios } from '../lib/api';

interface EquipmentDataPoint {
  timestamp: string;
  voltage_l1: number | null;
  voltage_l2: number | null;
  voltage_l3: number | null;
  current_l1: number | null;
  current_l2: number | null;
  current_l3: number | null;
  fuel_level: number | null;
  motor_hours: number | null;
  battery_status: number | null;
  ups_input_v: number | null;
  ups_output_v: number | null;
  frequency: number | null;
  device_type: string | null;
}

interface EquipmentResponse {
  data: EquipmentDataPoint[];
  metadata: {
    count: number;
    generatedAt: string;
  };
}

const PERIOD_OPTIONS = ['4h', '12h', '24h', '3d', '1w', '1M', '2M', '3M', '6M', '12M'] as const;
type PeriodOption = (typeof PERIOD_OPTIONS)[number];

const numberFormatter = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1
});

const timeFormatter = (value: string, period: PeriodOption): string => {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return value;
  if (['3d', '1w'].includes(period)) {
    return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
};

interface EquipmentWidgetProps {
  period?: PeriodOption;
  refreshKey?: number;
  hideHeader?: boolean;
  rackId?: string;
  location?: string;
  dateFrom?: string;
  dateTo?: string;
}

const EquipmentWidget: React.FC<EquipmentWidgetProps> = ({
  period: externalPeriod,
  refreshKey = 0,
  hideHeader = false,
  rackId,
  location,
  dateFrom,
  dateTo
}) => {
  const isMountedRef = useRef(true);
  const [internalPeriod] = useState<PeriodOption>('24h');
  const period = externalPeriod || internalPeriod;
  const [data, setData] = useState<EquipmentDataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => () => { isMountedRef.current = false; }, []);

  const fetchData = useCallback(async (selectedPeriod: PeriodOption, options?: { silent?: boolean }) => {
    if (!options?.silent) setLoading(true);
    setError(null);
    try {
      const params: any = { limit: 200 };
      if (dateFrom && dateTo) {
        params.from = dateFrom;
        params.to = dateTo;
      } else {
        params.period = selectedPeriod;
      }
      if (rackId) params.rack_id = rackId;
      if (location) params.location = location;

      const response = await axios.get<EquipmentResponse>('/api/equipment/timeseries', { params });
      if (!isMountedRef.current) return;
      setData(response.data.data);
    } catch (err) {
      console.error('Failed to fetch equipment data:', err);
      if (!isMountedRef.current) return;
      setError('Failed to load equipment data');
    } finally {
      if (!options?.silent && isMountedRef.current) setLoading(false);
    }
  }, [rackId, location, dateFrom, dateTo]);

  useEffect(() => {
    fetchData(period).catch(() => {});
    const interval = window.setInterval(() => {
      fetchData(period, { silent: true }).catch(() => {});
    }, 5 * 60 * 1000);
    return () => window.clearInterval(interval);
  }, [fetchData, period, refreshKey]);

  // Calculate latest values
  const latestData = data.length > 0 ? data[data.length - 1] : null;
  const avgVoltage = data.length > 0
    ? data.reduce((sum, d) => sum + ((d.voltage_l1 || 0) + (d.voltage_l2 || 0) + (d.voltage_l3 || 0)) / 3, 0) / data.length
    : null;

  const handleRefresh = () => fetchData(period).catch(() => {});

  return (
    <div className="card card-hover p-3 flex flex-col h-full overflow-hidden">
      {!hideHeader && (
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <Gauge className="h-4 w-4 text-indigo-600" />
            <h3 className="text-sm font-semibold text-gray-900">Equipment Monitoring</h3>
          </div>
          <button onClick={handleRefresh} className="btn-secondary text-xs flex items-center px-2 py-1">
            <RefreshCw className="h-3 w-3" />
          </button>
        </div>
      )}

      {error ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center py-4">
          <AlertCircle className="h-8 w-8 text-error-500 mb-2" />
          <p className="text-sm font-medium text-error-600">{error}</p>
          <button onClick={handleRefresh} className="btn-primary mt-2 text-xs">Retry</button>
        </div>
      ) : loading && !data.length ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center py-4">
          <div className="loading-spinner h-6 w-6 mb-2" />
          <p className="text-gray-500 text-sm">Loading...</p>
        </div>
      ) : data.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center py-4">
          <AlertCircle className="h-8 w-8 text-gray-400 mb-2" />
          <p className="text-gray-500 text-sm">No equipment data</p>
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-4 gap-1.5 mb-2">
            <div className="bg-yellow-50 rounded p-1.5">
              <p className="text-[9px] uppercase text-yellow-600 tracking-wide">Voltage</p>
              <p className="text-sm font-semibold text-gray-900">
                {avgVoltage !== null ? `${numberFormatter.format(avgVoltage)} V` : '--'}
              </p>
            </div>
            <div className="bg-blue-50 rounded p-1.5">
              <p className="text-[9px] uppercase text-blue-600 tracking-wide">Current</p>
              <p className="text-sm font-semibold text-gray-900">
                {latestData?.current_l1 !== null ? `${numberFormatter.format(latestData?.current_l1 || 0)} A` : '--'}
              </p>
            </div>
            <div className="bg-green-50 rounded p-1.5">
              <p className="text-[9px] uppercase text-green-600 tracking-wide">Fuel</p>
              <p className="text-sm font-semibold text-gray-900">
                {latestData?.fuel_level !== null ? `${numberFormatter.format(latestData?.fuel_level || 0)}%` : '--'}
              </p>
            </div>
            <div className="bg-purple-50 rounded p-1.5">
              <p className="text-[9px] uppercase text-purple-600 tracking-wide">Freq</p>
              <p className="text-sm font-semibold text-gray-900">
                {latestData?.frequency !== null ? `${numberFormatter.format(latestData?.frequency || 0)} Hz` : '--'}
              </p>
            </div>
          </div>

          {/* Voltage Chart */}
          <div className="mb-1">
            <h4 className="text-[10px] font-medium text-gray-600 mb-0.5">Voltage (L1, L2, L3)</h4>
            <ResponsiveContainer width="100%" height={115}>
              <LineChart data={data} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="timestamp" tickFormatter={(v) => timeFormatter(v, period)} tick={{ fontSize: 8, fill: '#6b7280' }} minTickGap={50} />
                <YAxis tick={{ fontSize: 8, fill: '#6b7280' }} domain={['auto', 'auto']} />
                <Tooltip labelFormatter={(v) => timeFormatter(String(v), period)} contentStyle={{ borderRadius: 6, fontSize: 9 }} />
                <Legend wrapperStyle={{ fontSize: 8, paddingTop: 0 }} />
                <Line type="monotone" dataKey="voltage_l1" name="L1" stroke="#f59e0b" strokeWidth={1.5} dot={false} />
                <Line type="monotone" dataKey="voltage_l2" name="L2" stroke="#10b981" strokeWidth={1.5} dot={false} />
                <Line type="monotone" dataKey="voltage_l3" name="L3" stroke="#3b82f6" strokeWidth={1.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Current Chart */}
          <div>
            <h4 className="text-[10px] font-medium text-gray-600 mb-0.5">Current (L1, L2, L3)</h4>
            <ResponsiveContainer width="100%" height={115}>
              <BarChart data={data.slice(-12)} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="timestamp" tickFormatter={(v) => timeFormatter(v, period)} tick={{ fontSize: 8, fill: '#6b7280' }} minTickGap={50} />
                <YAxis tick={{ fontSize: 8, fill: '#6b7280' }} />
                <Tooltip labelFormatter={(v) => timeFormatter(String(v), period)} contentStyle={{ borderRadius: 6, fontSize: 9 }} />
                <Legend wrapperStyle={{ fontSize: 8, paddingTop: 0 }} />
                <Bar dataKey="current_l1" name="L1" fill="#f59e0b" />
                <Bar dataKey="current_l2" name="L2" fill="#10b981" />
                <Bar dataKey="current_l3" name="L3" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
};

export default EquipmentWidget;
